import React, { useEffect, useId, useRef, useState } from "react";

import styles from "./dropdown.module.scss";

export type DropdownOption = { value: string; label: string };

// A select-only combobox: a button that opens a listbox. Focus stays on the
// button throughout and the highlighted option is named by
// aria-activedescendant, so a screen reader follows the arrow keys.
const Dropdown = ({
  labelId,
  describedBy,
  options,
  value,
  onChange,
  align = "start",
}: {
  // The visible label that names the control.
  labelId: string;
  // Text that adds to the label, read after it.
  describedBy?: string;
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  // Which edge of the button the list lines up with.
  align?: "start" | "end";
}) => {
  const id = useId();
  const listId = `${id}-list`;
  const root = useRef<HTMLSpanElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const selectedIndex = Math.max(
    options.findIndex((option) => option.value === value),
    0
  );

  useEffect(() => {
    if (!open) return undefined;
    // pointerdown, not mousedown: iOS Safari sends no mousedown for a tap on
    // something that isn't clickable, so the list would stay open.
    const away = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);

  useEffect(() => {
    const label = document.getElementById(labelId);
    if (!label) return undefined;
    const focus = () => trigger.current?.focus();
    label.addEventListener("click", focus);
    return () => label.removeEventListener("click", focus);
  }, [labelId]);

  const show = (index: number) => {
    setActive(index);
    setOpen(true);
  };

  const choose = (index: number) => {
    setOpen(false);
    trigger.current?.focus();
    if (options[index].value !== value) onChange(options[index].value);
  };

  const last = options.length - 1;

  const onKeyDown = (event: React.KeyboardEvent) => {
    const { key } = event;
    if (!open) {
      if (key === "ArrowDown" || key === "ArrowUp") {
        event.preventDefault();
        show(selectedIndex);
      } else if (key === "Enter" || key === " ") {
        event.preventDefault();
        show(selectedIndex);
      }
      return;
    }
    switch (key) {
      case "ArrowDown":
        event.preventDefault();
        setActive(Math.min(active + 1, last));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActive(Math.max(active - 1, 0));
        break;
      case "Home":
        event.preventDefault();
        setActive(0);
        break;
      case "End":
        event.preventDefault();
        setActive(last);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        choose(active);
        break;
      case "Escape":
        event.preventDefault();
        setOpen(false);
        break;
      case "Tab":
        setOpen(false);
        break;
      default:
    }
  };

  return (
    <span ref={root} className={styles["dropdown"]} data-align={align}>
      <button
        ref={trigger}
        type="button"
        role="combobox"
        className={styles["trigger"]}
        aria-labelledby={labelId}
        aria-describedby={describedBy}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open ? `${id}-${active}` : undefined}
        onClick={() => {
          if (open) {
            setOpen(false);
            return;
          }
          // Safari doesn't focus a button it clicks, so the arrow keys and
          // Escape would land on the page instead.
          trigger.current?.focus();
          show(selectedIndex);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => setOpen(false)}
      >
        <span className={styles["value"]}>{options[selectedIndex]?.label}</span>
        <svg
          className={styles["chevron"]}
          viewBox="0 0 16 16"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M4 6.5l4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <ul
        id={listId}
        role="listbox"
        className={styles["list"]}
        aria-labelledby={labelId}
        hidden={!open}
        // Keeps focus on the button while an option is clicked.
        onMouseDown={(event) => event.preventDefault()}
      >
        {open &&
          options.map((option, index) => (
            <li
              key={option.value}
              id={`${id}-${index}`}
              role="option"
              aria-selected={option.value === value}
              className={styles["option"]}
              data-active={index === active}
              onMouseEnter={() => setActive(index)}
              onClick={() => choose(index)}
            >
              <span>{option.label}</span>
              {option.value === value && (
                <svg
                  className={styles["check"]}
                  viewBox="0 0 16 16"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M3.5 8.5l3 3 6-7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </li>
          ))}
      </ul>
    </span>
  );
};

export default Dropdown;
